import './InternalToolsMenu.scss';
import cn from 'classnames';
import { ComponentPropsWithRef, ReactNode, useMemo, useState } from 'react';
import { Popover } from 'react-bootstrap';
import { FaBan } from 'react-icons/fa';
import { MdLanguage, MdLockOutline, MdOutlineExpandMore } from 'react-icons/md';
import { searchItems } from '@src/utils/search';
import { Brand50 } from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import {
  FullTertiaryNavItem,
  FullTertiaryNavSection,
  InternalToolsSectionKey,
  LocationType
} from '../../../utils/routing/location-constants';
import { useBpmNavigate } from '../../../utils/routing/useBpmNavigate';
import { Button } from '../../Button';
import { Appearance, Background, Size } from '../../designConstants';
import { SearchField } from '../../SearchField/SearchField';
import { ClientLogo } from '../ClientSelector/ClientLogo';
import { EmptySearch } from '../EmptySearch/EmptySearch';
import { NAV_ITEM_HEIGHT } from '../navbar-constants';
import { sortSectionNavs } from './internal-tools-menu-utils';

// `autoFocus` scrolls the body to the input while Popper hasn't placed the popover yet,
// which flashes the whole page up for a frame.
const focusWithoutScroll = (input: HTMLInputElement | null) => {
  input?.focus({ preventScroll: true });
};

type InternalToolsMenuProps = Omit<ComponentPropsWithRef<typeof Popover>, 'id'> & {
  prettyName: string;
  sections: FullTertiaryNavSection[];
  globalIcon: ReactNode;
  isTertiary?: boolean;
};

export const InternalToolsMenu = ({
  prettyName,
  sections,
  globalIcon,
  isTertiary = true,
  className,
  ...overlayProps
}: InternalToolsMenuProps): ReactNode => {
  const { cid } = useCompanyInfo();
  const [searchQuery, setSearchQuery] = useState('');

  const itemCount = useMemo(
    () => sections.reduce((sum, section) => sum + section.navs.length, 0),
    [sections]
  );

  const sortedSections = useMemo(() => sortSectionNavs(sections), [sections]);

  const filteredSections = useMemo(() => {
    if (!searchQuery) return sortedSections;
    return sortedSections
      .map(section => ({
        ...section,
        navs: searchItems(section.navs, searchQuery, { keys: ['prettyName'], rank: false })
      }))
      .filter(section => section.navs.length > 0);
  }, [sortedSections, searchQuery]);

  const clientSections = filteredSections.filter(s => s.type === LocationType.Client);
  const globalSections = filteredSections.filter(s => s.type !== LocationType.Client);

  const clientIcon = cid ? <ClientLogo cid={cid} background={Background.DARK} /> : <MdLanguage />;

  const hasNoResults =
    filteredSections.every(section => section.navs.length === 0) && Boolean(searchQuery);

  const isFiltering = Boolean(searchQuery);

  return (
    <Popover
      {...overlayProps}
      id='internal-tools-popover'
      className={cn('nav-popover nav-group-popover internal-tools-popover', className, {
        tertiary: isTertiary
      })}
    >
      <Popover.Header>
        <h3 className='nav-group-header'>
          <span className='nav-group-icon'>
            <MdLockOutline color={Brand50} />
          </span>
          <span className='nav-group-title'>{prettyName}</span>
        </h3>
      </Popover.Header>
      <Popover.Body>
        {itemCount > 5 && (
          <div className='internal-tools-menus-search'>
            <SearchField
              size={Size.LG}
              id='internal-tools-search'
              onChange={e => {
                setSearchQuery(e.currentTarget.value);
              }}
              value={searchQuery}
              background={Background.DARK}
              placeholder='Search internal tools...'
              ref={focusWithoutScroll}
            />
          </div>
        )}
        <div className='internal-tools-menus-body'>
          {hasNoResults ? (
            <EmptySearch />
          ) : (
            <>
              <ToolsMenu
                sections={clientSections}
                icon={clientIcon}
                title='Client Management'
                subtitle='Client-specific tools'
                emptyMessage={`No client-specific tools for ${prettyName}`}
                isFiltering={isFiltering}
              />
              <ToolsMenu
                sections={globalSections}
                icon={globalIcon}
                title={`Global ${prettyName}`}
                subtitle='Tinuiti-wide tools'
                emptyMessage={`No global tools for ${prettyName}`}
                isFiltering={isFiltering}
              />
            </>
          )}
        </div>
      </Popover.Body>
    </Popover>
  );
};

interface ToolsMenuProps {
  sections: FullTertiaryNavSection[];
  icon: ReactNode;
  title: string;
  subtitle: string;
  emptyMessage: string;
  isFiltering: boolean;
}

const ToolsMenu = ({
  sections,
  icon,
  title,
  subtitle,
  emptyMessage,
  isFiltering
}: ToolsMenuProps): ReactNode => {
  const isEmpty = sections.every(section => section.navs.length === 0);
  const [prevIsFiltering, setPrevIsFiltering] = useState(isFiltering);

  if (prevIsFiltering !== isFiltering) {
    setPrevIsFiltering(isFiltering);
  }

  const body = useMemo(() => {
    if (isEmpty && !isFiltering) {
      return <EmptySearch icon={FaBan} label={emptyMessage} />;
    }

    if (isEmpty) {
      return <EmptySearch />;
    }

    return sections.map((section, i) => {
      const standalone = sections.length === 1;
      return (
        <ToolsMenuSection
          key={`${title}-tools-menu-section-${i}`}
          section={section}
          standalone={standalone}
          isFiltering={isFiltering}
        />
      );
    });
  }, [emptyMessage, isEmpty, isFiltering, sections, title]);

  return (
    <div className={cn('tools-menu', { empty: isEmpty })}>
      <div className='tools-menu-header'>
        <div className='tools-menu-icon'>{icon}</div>
        <div className='tools-menu-label'>
          <div className='tools-menu-title'>{title}</div>
          <div className='tools-menu-subtitle'>{subtitle}</div>
        </div>
      </div>
      <div className='tools-menu-body'>{body}</div>
    </div>
  );
};

interface ToolsMenuSectionProps {
  section: FullTertiaryNavSection;
  standalone: boolean;
  isFiltering: boolean;
}

const ToolsMenuSection = ({
  section,
  standalone,
  isFiltering
}: ToolsMenuSectionProps): ReactNode => {
  const { sectionLabel, navs } = section;
  const [manuallyExpanded, setManuallyExpanded] = useState(
    standalone ||
      (sectionLabel &&
        [InternalToolsSectionKey.REPORTING, InternalToolsSectionKey.OPERATIONS].includes(
          sectionLabel
        ))
  );
  // A search query should always reveal matches regardless of the section's manual
  // collapsed/expanded state — otherwise a filtered-in item can stay hidden at height 0.
  const expanded = isFiltering || manuallyExpanded;

  const bodyHeight = useMemo(
    () => (expanded ? NAV_ITEM_HEIGHT * navs.length : 0),
    [expanded, navs.length]
  );

  if (navs.length === 0) {
    return undefined;
  }

  return (
    <div className={cn('tools-menu-section', { expanded, collapsed: !expanded })}>
      {!standalone && (
        <Button
          size={Size.LG}
          data-testid='tools-section-collapse-btn'
          className='tools-menu-section-header'
          appearance={Appearance.EMPTY}
          background={Background.DARK}
          trailingElement={<MdOutlineExpandMore />}
          onClick={() => setManuallyExpanded(prev => !prev)}
        >
          {sectionLabel}
        </Button>
      )}
      <div className='tools-menu-section-body' style={{ height: bodyHeight }}>
        {navs.map(item => (
          <ToolsMenuItem key={item.key} item={item} />
        ))}
      </div>
    </div>
  );
};

interface ToolsMenuItemProps {
  item: FullTertiaryNavItem;
}

const ToolsMenuItem = ({ item }: ToolsMenuItemProps): ReactNode => {
  const navigate = useBpmNavigate();
  const { prettyName, icon, fullPath, type } = item;

  return (
    <Button
      size={Size.LG}
      appearance={Appearance.EMPTY}
      background={Background.DARK}
      leadingElement={icon}
      className='tools-menu-item'
      onClick={() => navigate({ type, path: fullPath, prettyName })}
    >
      {prettyName}
    </Button>
  );
};
